import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { COMPANIONS_NAMESPACE } from '@/i18n';
import type { CompanionRow } from '@/Pages/Companions/hooks/useCompanions';
import { isKnownMinorCompanion } from '@/Pages/Companions/utils/companionAge';
import { formatBirthDate } from '@/Pages/Companions/utils/formatBirthDate';

type Props = {
  row: CompanionRow;
  /** Foto desta linha subindo agora. */
  isUploading: boolean;
  /** Exclusão desta linha em andamento. */
  isRemoving: boolean;
  /** Outra ação ocupando o formulário/lista — evita dois envios ao mesmo tempo. */
  busy: boolean;
  onPhoto: (file: File | null) => void;
  onShare: () => void;
  onEdit: () => void;
  onRemove: () => void;
};

/**
 * Uma linha do grupo: o que já está preenchido, o que falta e o que o titular
 * pode fazer agora. Editar/excluir só aparecem enquanto `row.editable` — quem
 * já entrou no evento (ou está a menos de 2h dele) vira leitura, com o motivo
 * escrito na própria linha em vez de botões que dariam 409.
 */
export function CompanionRowItem({
  row,
  isUploading,
  isRemoving,
  busy,
  onPhoto,
  onShare,
  onEdit,
  onRemove,
}: Props): JSX.Element {
  const { t } = useTranslation(COMPANIONS_NAMESPACE);

  // Service anterior a esta feature não manda `editable`/`lockedReason`. Sem os
  // dois checks explícitos, `undefined` esconderia os botões E ainda escreveria
  // "alterações encerradas" em toda linha — mentira em cima de degradação.
  const canMutate = row.editable === true;
  const lockedReason = row.lockedReason ?? null;

  // Com 16 anos ou menos o acompanhante não faz o próprio cadastro: a foto é
  // do titular. Só dá para afirmar isso com a data de nascimento preenchida —
  // vaga sem data mantém o "Compartilhar" e a seção explica a regra.
  const isMinor = isKnownMinorCompanion(row.birthDate);

  const birthDate = formatBirthDate(row.birthDate);
  const details = [
    birthDate === null ? null : t('rowBirthDate', { value: birthDate }),
    row.documentMasked === null ? null : t('rowDocument', { value: row.documentMasked }),
    row.email === null ? null : t('rowEmail', { value: row.email }),
  ].filter((line): line is string => line !== null);

  return (
    <ListItem disableGutters sx={{ display: 'block', py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ListItemText
          primary={row.fullName || t('unnamedCompanion')}
          secondary={
            row.status === 'enrolled'
              ? t('statusEnrolled')
              : isMinor
                ? t('statusAwaitingMinor')
                : row.hasDocument
                  ? t('statusAwaitingByDocument')
                  : t('statusAwaitingByLink')
          }
        />
        {row.status === 'enrolled' && (
          <Chip size="small" color="success" label={t('statusChipReady')} />
        )}
      </Box>

      {/* O que já está preenchido — é isto que o titular confere antes de decidir
          se edita ou se manda a foto e encerra. */}
      <Typography
        component="p"
        variant="caption"
        color="text.secondary"
        data-testid={`companion-details-${row.id}`}
      >
        {details.length > 0 ? details.join(' · ') : t('rowNoData')}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
        {row.status === 'awaiting_selfie' && (
          <>
            <Button
              size="small"
              variant="outlined"
              component="label"
              disabled={busy}
              data-testid={`companion-photo-${row.id}`}
            >
              {isUploading ? t('submitLoading') : t('rowPhotoCta')}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(event) => onPhoto(event.target.files?.[0] ?? null)}
              />
            </Button>
            {!row.hasDocument && !isMinor && (
              <Button size="small" onClick={onShare} data-testid={`companion-share-${row.id}`}>
                {t('shareCta')}
              </Button>
            )}
          </>
        )}

        {canMutate && (
          <>
            <Button
              size="small"
              onClick={onEdit}
              disabled={busy}
              data-testid={`companion-edit-${row.id}`}
            >
              {t('editCta')}
            </Button>
            <Button
              size="small"
              color="error"
              onClick={onRemove}
              disabled={busy}
              data-testid={`companion-remove-${row.id}`}
            >
              {isRemoving ? t('removeLoading') : t('removeCta')}
            </Button>
          </>
        )}
      </Box>

      {lockedReason !== null && (
        <Typography
          component="p"
          variant="caption"
          color="text.secondary"
          data-testid={`companion-locked-${row.id}`}
        >
          {lockedReason === 'checked_in' ? t('lockedCheckedIn') : t('lockedEventStarting')}
        </Typography>
      )}
    </ListItem>
  );
}
